import qrcode from 'qrcode-generator'

/** QR-code als SVG (geen afhankelijkheden, scherp op elk formaat), in de huisstijlkleuren. */
function qrSvgDataUrl(text: string): string {
  const qr = qrcode(0, 'M')
  qr.addData(text)
  qr.make()
  const n = qr.getModuleCount()
  const margin = 1
  const size = n + margin * 2
  let path = ''
  for (let r = 0; r < n; r++) for (let c = 0; c < n; c++) if (qr.isDark(r, c)) path += `M${c + margin} ${r + margin}h1v1h-1z`
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${size} ${size}" shape-rendering="crispEdges"><rect width="${size}" height="${size}" fill="#fcfaf7"/><path d="${path}" fill="#241f1c"/></svg>`
  return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`
}
import { readLocalJson, readSession, writeLocalJson, writeSession } from '../core/storage'

/**
 * DEMO — tweestapsverificatie zoals Laravel Fortify hem op de server doet, maar in de browser.
 * Een echte TOTP (RFC 6238): wie de QR-code scant met een authenticator-app, krijgt codes die
 * hier kloppen. Zo kan Sarah het laten zien zonder server. Op de server staat de sleutel
 * versleuteld in de database; hier in localStorage, dus dit is GEEN beveiliging.
 */

const STATE_KEY = 'bb-demo-admin-2fa'
const PENDING_KEY = 'bb-demo-admin-2fa-pending'
const ISSUER = 'Beauty Bewust'
const STEP_MS = 30_000
const B32 = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ234567'

type State = { secret: string | null; enabled: boolean; recoveryCodes: string[] }
const EMPTY: State = { secret: null, enabled: false, recoveryCodes: [] }

const read = (): State => ({ ...EMPTY, ...readLocalJson<Partial<State>>(STATE_KEY, {}) })
const write = (s: State) => writeLocalJson(STATE_KEY, s)

function randomSecret(): string {
  const bytes = crypto.getRandomValues(new Uint8Array(20))
  let bits = ''
  bytes.forEach((b) => (bits += b.toString(2).padStart(8, '0')))
  let out = ''
  for (let i = 0; i + 5 <= bits.length; i += 5) out += B32[parseInt(bits.slice(i, i + 5), 2)]
  return out
}

function decodeSecret(secret: string): Uint8Array {
  let bits = ''
  for (const ch of secret) bits += B32.indexOf(ch).toString(2).padStart(5, '0')
  const bytes = new Uint8Array(Math.floor(bits.length / 8))
  for (let i = 0; i < bytes.length; i++) bytes[i] = parseInt(bits.slice(i * 8, i * 8 + 8), 2)
  return bytes
}

async function totp(secret: string, at: number): Promise<string> {
  const counter = Math.floor(at / STEP_MS)
  const msg = new DataView(new ArrayBuffer(8))
  msg.setUint32(0, Math.floor(counter / 2 ** 32))
  msg.setUint32(4, counter >>> 0)
  const key = await crypto.subtle.importKey('raw', decodeSecret(secret), { name: 'HMAC', hash: 'SHA-1' }, false, ['sign'])
  const sig = new Uint8Array(await crypto.subtle.sign('HMAC', key, msg.buffer))
  const o = sig[sig.length - 1] & 0x0f
  const n = ((sig[o] & 0x7f) << 24) | (sig[o + 1] << 16) | (sig[o + 2] << 8) | sig[o + 3]
  return String(n % 1_000_000).padStart(6, '0')
}

/** Eén stap speling naar voren en naar achteren, net als Fortify (klok van de telefoon). */
async function verifyCode(secret: string, code: string): Promise<boolean> {
  const now = Date.now()
  for (const drift of [-1, 0, 1]) if ((await totp(secret, now + drift * STEP_MS)) === code) return true
  return false
}

const newRecoveryCodes = () =>
  Array.from({ length: 8 }, () => {
    const hex = Array.from(crypto.getRandomValues(new Uint8Array(5)), (b) => b.toString(16).padStart(2, '0')).join('')
    return `${hex.slice(0, 5)}-${hex.slice(5)}`
  })

export const demoTwoFactor = {
  enabled: () => read().enabled,

  /** Wachtwoord klopte: wacht op de tweede stap (per tabblad, zoals een sessie). */
  setPending: (on: boolean) => writeSession(PENDING_KEY, on ? '1' : ''),
  pending: () => readSession(PENDING_KEY) === '1',

  async setup(email: string) {
    const secret = randomSecret()
    write({ ...read(), secret, enabled: false })
    const uri = `otpauth://totp/${encodeURIComponent(`${ISSUER}:${email}`)}?secret=${secret}&issuer=${encodeURIComponent(ISSUER)}&period=30&digits=6`
    return { qrImage: qrSvgDataUrl(uri), secret }
  },

  /** Bevestigen met de eerste code: daarna staat 2FA aan. `null` = code klopt niet. */
  async confirm(code: string): Promise<string[] | null> {
    const s = read()
    if (!s.secret || !(await verifyCode(s.secret, code))) return null
    const recoveryCodes = newRecoveryCodes()
    write({ secret: s.secret, enabled: true, recoveryCodes })
    return recoveryCodes
  },

  async check(req: { code: string } | { recoveryCode: string }): Promise<boolean> {
    const s = read()
    if (!s.enabled || !s.secret) return false
    if ('code' in req) return verifyCode(s.secret, req.code)
    const wanted = req.recoveryCode.trim().toLowerCase()
    if (!s.recoveryCodes.includes(wanted)) return false
    // Eenmalig: een gebruikte herstelcode vervalt.
    write({ ...s, recoveryCodes: s.recoveryCodes.filter((c) => c !== wanted) })
    return true
  },

  regenerate(): string[] {
    const recoveryCodes = newRecoveryCodes()
    write({ ...read(), recoveryCodes })
    return recoveryCodes
  },

  /** Voor het inlogscherm in de demo: de code die de app nu zou tonen. */
  async currentCode(): Promise<string | null> {
    const s = read()
    return s.enabled && s.secret ? totp(s.secret, Date.now()) : null
  },
}
